/**
 * Game Answer operations
 *
 * Functions for managing player answers in the normalized game_answer table.
 */

import { SupabaseClient } from "@supabase/supabase-js"
import { getClient } from "../get-client"
import type { Answer } from "../../../types"

interface ExistingAnswer {
  points: number
  answer: string
}

interface SaveAnswerData {
  gameId: string
  playerId: string
  stockId: string
  questionIndex: number
  answer: string
  points: number
}

interface SaveAnswerResult {
  error: Error | null
}

export interface SubmitAnswerParams {
  token: string
  playerId: string
  questionIndex: number
  answer: string
  points: number
  stockId?: string | null
  /**
   * Whether the stock row should receive the answer points (classic mode).
   * Set to false for event mode so the RPC does not try to UPDATE stock with
   * an id that lives in event_photo.
   */
  updateStockPoints?: boolean
}

export interface SubmitAnswerResult {
  success: boolean
  reason: string | null
  wasDuplicate: boolean
  currentStreak: number
  totalPoints: number
  storedPoints: number
}

interface SubmitAnswerRpcRow {
  success: boolean
  reason: string | null
  was_duplicate: boolean
  current_streak: number
  total_points: number
  stored_points: number
}

/**
 * Atomic submit_answer (audit DB / M1).
 *
 * Calls the PL/pgSQL submit_answer() function which merges the
 * findExistingAnswer + getPlayerStreak + saveAnswer + increment_stock_points
 * round-trips into one transactional RPC. Returns null when the RPC is
 * unavailable or fails so the caller can fall back to the legacy pipeline
 * (mirrors the pattern used for tick_game in src/lib/supabase/tick).
 *
 * Point calculation is intentionally NOT moved to SQL: see migration 035.
 */
export const submitAnswerRpc = async (
  params: SubmitAnswerParams,
  supabase: SupabaseClient | null = null
): Promise<SubmitAnswerResult | null> => {
  const client = supabase || getClient()

  // For event mode the stock_id is not a stock UUID -> never forward it.
  const stockArg =
    params.updateStockPoints === false ? null : params.stockId || null

  try {
    const { data, error } = await client.rpc("submit_answer", {
      p_token: params.token,
      p_player_id: params.playerId,
      p_question_index: params.questionIndex,
      p_answer: params.answer,
      p_points: params.points,
      p_stock_id: stockArg,
    })

    if (error) {
      console.warn(
        "[submit_answer] RPC failed, using legacy fallback:",
        error.message
      )
      return null
    }

    const row: SubmitAnswerRpcRow | undefined = Array.isArray(data)
      ? data[0]
      : data
    if (!row) return null

    return {
      success: row.success,
      reason: row.reason,
      wasDuplicate: row.was_duplicate,
      currentStreak: row.current_streak,
      totalPoints: row.total_points,
      storedPoints: row.stored_points,
    }
  } catch (e) {
    console.warn(
      "[submit_answer] RPC threw, using legacy fallback:",
      (e as Error).message
    )
    return null
  }
}

/**
 * Gets the player's current streak (consecutive correct answers).
 * Calculates from the most recent answers going backwards.
 *
 * @param gameId - Game ID
 * @param playerId - Player ID
 * @param beforeIndex - Count streak up to (not including) this index
 * @param supabase - Optional Supabase client
 * @returns Current streak count
 */
export const getPlayerStreak = async (
  gameId: string,
  playerId: string,
  beforeIndex: number,
  supabase: SupabaseClient | null = null
): Promise<number> => {
  const client = supabase || getClient()

  // Get all answers for this player in this game, ordered by question index desc
  const { data: answers } = await client
    .from("game_answer")
    .select("question_index, points")
    .eq("game_id", gameId)
    .eq("player_id", playerId)
    .lt("question_index", beforeIndex)
    .order("question_index", { ascending: false })

  if (!answers || answers.length === 0) return 0

  // Count consecutive positive-point answers from the most recent
  let streak = 0
  for (const answer of answers) {
    if (answer.points > 0) {
      streak++
    } else {
      break // Streak broken
    }
  }

  return streak
}

/**
 * Checks for an existing answer from the player for a question.
 *
 * @param gameId - Game ID
 * @param playerId - Player ID
 * @param questionIndex - Current question index
 * @param supabase - Optional Supabase client
 * @returns Existing answer with points, or null
 */
export const findExistingAnswer = async (
  gameId: string,
  playerId: string,
  questionIndex: number,
  supabase: SupabaseClient | null = null
): Promise<ExistingAnswer | null> => {
  const client = supabase || getClient()

  const { data } = await client
    .from("game_answer")
    .select("points, answer")
    .eq("game_id", gameId)
    .eq("player_id", playerId)
    .eq("question_index", questionIndex)
    .single()

  return data as ExistingAnswer | null
}

/**
 * Saves an answer to the normalized game_answer table.
 *
 * @param answerData - Answer data to save
 * @param supabase - Optional Supabase client
 * @returns Error object or null
 */
export const saveAnswer = async (
  answerData: SaveAnswerData,
  supabase: SupabaseClient | null = null
): Promise<SaveAnswerResult> => {
  const client = supabase || getClient()
  const { gameId, playerId, stockId, questionIndex, answer, points } =
    answerData

  const { error } = await client.from("game_answer").upsert(
    {
      game_id: gameId,
      player_id: playerId,
      stock_id: stockId,
      question_index: questionIndex,
      answer,
      points,
    },
    { onConflict: "game_id,player_id,question_index" }
  )

  return { error }
}

/**
 * Fetch all answers for a game (audit M8 / B7).
 *
 * Replaces reads from the legacy `game.answers` JSONB column. Returns
 * the same `Answer` shape consumers (PlayersList, WinnerCelebration,
 * EndScreen, useWinner) already understand.
 */
export const getGameAnswers = async (
  gameId: string,
  supabase: SupabaseClient | null = null
): Promise<Answer[]> => {
  const client = supabase || getClient()
  const { data, error } = await client
    .from("game_answer")
    .select("player_id, question_index, answer, points")
    .eq("game_id", gameId)
    .order("question_index", { ascending: true })

  if (error) {
    console.error("[getGameAnswers] fetch error:", error)
    return []
  }

  return (data || []).map(row => ({
    playerId: String(row.player_id),
    index: row.question_index as number,
    answer: (row.answer ?? "") as string,
    points: (row.points ?? 0) as number,
  }))
}
